import React, { useEffect } from 'react'
import { useGlobal } from 'reactn'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Button, FormControl, FormHelperText } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { parse } from 'date-fns'
import { EligibilityVerificationUrlSettingProps } from 'types/applicant'
import Empty from 'ui/pages/med/Empty'
import { formatShortDateString } from 'ui/utils/dateFormatUtils'
import { originUrl } from 'config/url'

export const eligibilityVerificationCallbackUrlPath =
  '/personal/eligibility-verification/'

const windowName = 'yq_online_eligibility_verification'

export function replaceToCancelUrl(url: string): string {
  const urlObj = new URL(url)

  const pathSegments = urlObj.pathname.split('/').filter((s) => !!s)

  // URLの最後がindex.htmlの場合はwithdraw.htmlに変更
  if (pathSegments[pathSegments.length - 1] === 'index.html') {
    pathSegments[pathSegments.length - 1] = 'withdraw.html'
  } else {
    pathSegments.push('withdraw.html')
  }

  // URLオブジェクトのpathnameを更新
  urlObj.pathname = pathSegments.join('/')

  return urlObj.toString()
}

export function buildEligibilityVerificationUrl({
  setting,
  reservationDate,
  callbackPath,
  mode = 'verification',
}: {
  setting?: EligibilityVerificationUrlSettingProps
  reservationDate?: Date
  callbackPath: string
  mode?: 'verification' | 'cancel'
}): string | undefined {
  const { url, appId } = setting || {}
  if (!url || !appId || !reservationDate) return undefined

  const callbackUrl = `${originUrl}${callbackPath}` //コールバックURL
  const reqType = '1'
  const isSendEpx = '1'
  const clinicDate = formatShortDateString(reservationDate)

  const urlObj = new URL(url)
  // NOTE: 登録されたURLからはmi_codeパラメーターのみを使用する
  const miCode = urlObj.searchParams.get('mi_code') || ''
  const completeUrl = `${urlObj.origin}${
    urlObj.pathname
  }?mi_code=${miCode}&app_id=${appId}&rtn_url=${btoa(
    callbackUrl
  )}&req_type=${reqType}&clinic_date=${clinicDate}&is_send_epx=${isSendEpx}`

  if (mode === 'cancel') {
    // NOTE: path以外のパラメーターは登録と同じ前提
    return replaceToCancelUrl(completeUrl)
  }
  return completeUrl
}

export type VerificationResponse = {
  result_code?: string
  error_code?: string
}

// TODO: Promiseを返すようにする
const connectEligibilityVerification = ({
  setting,
  reservationDate,
}: {
  setting?: EligibilityVerificationUrlSettingProps
  reservationDate?: Date
}): boolean => {
  let result = false
  const targetUrl = buildEligibilityVerificationUrl({
    setting,
    reservationDate,
    callbackPath: eligibilityVerificationCallbackUrlPath,
  })
  if (targetUrl) {
    const childWindow = window.open(targetUrl, windowName, undefined)
    result = !!childWindow
  }
  return result
}

// 予約画面で使う、オンライン資格確認画面への遷移ボタン
export const EligibilityVerificationButton = ({
  setting,
  reservationDateString,
  isAgreed,
}: {
  setting?: EligibilityVerificationUrlSettingProps
  reservationDateString?: string
  isAgreed?: boolean
}) => {
  const { t } = useTranslation()
  const noReservationDateString: boolean = !reservationDateString
  const reservationDate = reservationDateString
    ? parse(reservationDateString, 'yyyy-MM-dd HH:mm:ss', new Date())
    : undefined
  return (
    <FormControl error={noReservationDateString}>
      <Button
        variant="contained"
        color={noReservationDateString ? 'error' : 'primary'}
        onClick={() => {
          connectEligibilityVerification({
            setting,
            reservationDate,
          })
        }}
        disabled={noReservationDateString || isAgreed}
      >
        {isAgreed ? t('同意済み') : t('閲覧同意へ進む')}
      </Button>
      {noReservationDateString && (
        <FormHelperText>
          {t('validation.予約日時を選択してください')}
        </FormHelperText>
      )}
    </FormControl>
  )
}

// オンライン資格確認画面からのコールバック画面からMessageEventを受け取る
export const EligibilityVerificationMessageApi = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void
  onError: (message: React.ReactNode) => void
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    window.addEventListener('message', receiveMessage, false)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const receiveMessage = (event: MessageEvent) => {
    if (event.origin !== originUrl) {
      return
    }
    if (event.data?.[windowName]) {
      const { result_code, error_code }: VerificationResponse =
        event.data[windowName]

      if (result_code === '00') {
        onSuccess()
      } else {
        let errorMessage = t('errors.システムエラーが発生しました')
        switch (error_code) {
          case '01':
            errorMessage = t('errors.マイナ保険証が未登録です')
            break
          case '02':
            errorMessage = t(
              'errors.同一医療機関等の未来日の予約診療があるため処理を完了できませんでした'
            )
            break
          case '03':
            errorMessage = t('errors.有効な資格情報が確認できませんでした')
            break
          default:
            break
        }
        onError(errorMessage)
      }
    }
  }
  return null
}

// オンライン資格確認画面からのコールバック画面
export const OnlineEligibilityVerificationCallbackScreen: React.FC = () => {
  const [, setError] = useGlobal('error')
  const location = useLocation()
  const query = queryString.parse(location.search)

  const currentWindowName = window.name
  const isValidWindowName = currentWindowName === windowName

  useEffect(() => {
    if (isValidWindowName) {
      const response: VerificationResponse = {
        result_code: query?.result_code as string,
        error_code: query?.error_code as string,
      }

      window?.opener?.postMessage(
        {
          [windowName]: response,
        },
        originUrl
      )
      window?.close?.()
    } else {
      setError('forbidden')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return <Empty />
}
